import React, { memo } from 'react';

/**
 * TenureBadge Component
 * 
 * A beautiful, animated SVG badge that displays years of service
 * with color-coded tiers based on tenure length.
 * 
 * UNIFIED SIZING SYSTEM (matches CustomBadge and DesignationBadge):
 * - small: 32px (mobile/compact views)
 * - medium: 40px (default, dashboard sidebar)
 * - large: 48px (profile modal, detail views)
 * 
 * Color Tiers:
 * - 1-2 years: Yellow/Gold
 * - 3-4 years: Orange
 * - 5-6 years: Red
 * - 7-9 years: Blue
 * - 10-11 years: Green
 * - 12-14 years: Purple
 * - 15+ years: Rainbow (animated)
 * 
 * @param {number} years - Years of service (calculated from first_working_day)
 * @param {string} size - 'small' (32px), 'medium' (40px), or 'large' (48px)
 * @param {boolean} showLabel - Whether to show "X Years" label below badge
 * @param {boolean} animate - Whether to enable hover/sparkle animations
 */

const TenureBadge = ({ years, size = 'medium', showLabel = true, animate = true }) => {
  // UNIFIED size configurations - matches CustomBadge/DesignationBadge
  // badge = actual pixel size of the badge container
  // number = SVG text fontSize (relative to 100x100 viewBox) - MUST BE LARGE for visibility
  // For a 100x100 viewBox, fontSize of 55-60 fills ~60% of the badge
  const sizes = {
    small: { badge: 32, number: 50, label: 9, ring: 2 },    // SVG fontSize 50 in 100x100 viewBox
    medium: { badge: 40, number: 55, label: 10, ring: 2.5 }, // SVG fontSize 55 - BIG and BOLD
    large: { badge: 48, number: 60, label: 11, ring: 3 },    // SVG fontSize 60 - EXTRA LARGE
  };
  
  const s = sizes[size] || sizes.medium;
  
  // Color tiers based on years (no tier names exposed)
  const getTierConfig = (yrs) => {
    if (yrs >= 15) return { 
      gradient: 'url(#rainbowGradient)',
      bgGradient: 'linear-gradient(135deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3, #54a0ff, #5f27cd)',
      glow: 'rgba(255, 107, 107, 0.5)',
      textColor: '#fff',
      tier: 7
    };
    if (yrs >= 12) return { 
      gradient: 'url(#purpleGradient)',
      bgGradient: 'linear-gradient(135deg, #9333EA, #A855F7)',
      glow: 'rgba(147, 51, 234, 0.5)',
      textColor: '#fff',
      tier: 6
    };
    if (yrs >= 10) return { 
      gradient: 'url(#greenGradient)',
      bgGradient: 'linear-gradient(135deg, #16A34A, #22C55E)',
      glow: 'rgba(22, 163, 74, 0.5)',
      textColor: '#fff',
      tier: 5
    };
    if (yrs >= 7) return { 
      gradient: 'url(#blueGradient)',
      bgGradient: 'linear-gradient(135deg, #2563EB, #3B82F6)',
      glow: 'rgba(37, 99, 235, 0.5)',
      textColor: '#fff',
      tier: 4
    };
    if (yrs >= 5) return { 
      gradient: 'url(#redGradient)',
      bgGradient: 'linear-gradient(135deg, #DC2626, #EF4444)',
      glow: 'rgba(220, 38, 38, 0.5)',
      textColor: '#fff',
      tier: 3
    };
    if (yrs >= 3) return { 
      gradient: 'url(#orangeGradient)',
      bgGradient: 'linear-gradient(135deg, #EA580C, #F97316)',
      glow: 'rgba(234, 88, 12, 0.5)',
      textColor: '#fff',
      tier: 2
    };
    return { 
      gradient: 'url(#yellowGradient)',
      bgGradient: 'linear-gradient(135deg, #EAB308, #FACC15)',
      glow: 'rgba(234, 179, 8, 0.5)',
      textColor: '#1a1a1a',
      tier: 1
    };
  };
  
  const tier = getTierConfig(years);
  const displayYears = Math.floor(years);
  
  // Don't render if years is 0 or invalid
  if (!years || years < 1) {
    return null;
  }
  
  return (
    <div 
      className="inline-flex flex-col items-center gap-1" 
      data-testid="tenure-badge"
      title={`${displayYears} ${displayYears === 1 ? 'year' : 'years'} of service`}
    >
      {/* Badge SVG */}
      <div 
        className={`relative ${animate ? 'group' : ''}`}
        style={{ width: s.badge, height: s.badge }}
      >
        <svg 
          viewBox="0 0 100 100" 
          className={`w-full h-full ${animate ? 'transition-transform duration-300 group-hover:scale-110' : ''}`}
        >
          <defs>
            {/* Gradient Definitions */}
            <linearGradient id="yellowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#EAB308" />
              <stop offset="100%" stopColor="#FDE047" />
            </linearGradient>
            <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#EA580C" />
              <stop offset="100%" stopColor="#FB923C" />
            </linearGradient>
            <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#DC2626" />
              <stop offset="100%" stopColor="#F87171" />
            </linearGradient>
            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2563EB" />
              <stop offset="100%" stopColor="#60A5FA" />
            </linearGradient>
            <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#16A34A" />
              <stop offset="100%" stopColor="#4ADE80" />
            </linearGradient>
            <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#9333EA" />
              <stop offset="100%" stopColor="#C084FC" />
            </linearGradient>
            <linearGradient id="rainbowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FF6B6B" />
              <stop offset="20%" stopColor="#FECA57" />
              <stop offset="40%" stopColor="#48DBFB" />
              <stop offset="60%" stopColor="#FF9FF3" />
              <stop offset="80%" stopColor="#54A0FF" />
              <stop offset="100%" stopColor="#5F27CD" />
            </linearGradient>
          </defs>
          
          {/* Main ring - solid without glow */}
          <circle 
            cx="50" 
            cy="50" 
            r="44" 
            fill="none" 
            stroke={tier.gradient}
            strokeWidth={s.ring + 2}
          />
          
          {/* Inner circle background - solid */}
          <circle 
            cx="50" 
            cy="50" 
            r="38" 
            fill={tier.gradient}
          />
          
          {/* Subtle shine effect */}
          <ellipse
            cx="40"
            cy="30"
            rx="12"
            ry="6"
            fill="white"
            opacity="0.12"
          />
          
          {/* Year number - LARGE, BOLD, PROMINENT - perfectly centered */}
          <text 
            x="50" 
            y="44" 
            textAnchor="middle" 
            dominantBaseline="central"
            alignmentBaseline="central"
            fill={tier.textColor}
            fontSize={s.number}
            fontWeight="900"
            fontFamily="Arial Black, Arial, sans-serif"
            letterSpacing="-2"
          >
            {displayYears}
          </text>
          
          {/* Decorative dots for higher tiers (7+ years) */}
          {tier.tier >= 4 && (
            <>
              <circle cx="50" cy="18" r="3" fill={tier.textColor} opacity="0.8" />
              <circle cx="82" cy="50" r="3" fill={tier.textColor} opacity="0.8" />
              <circle cx="50" cy="82" r="3" fill={tier.textColor} opacity="0.8" />
              <circle cx="18" cy="50" r="3" fill={tier.textColor} opacity="0.8" />
            </>
          )}
          
          {/* Star decoration for Legend tier (15+ years) */}
          {tier.tier >= 7 && (
            <polygon 
              points="50,5 52,12 59,12 54,17 56,24 50,20 44,24 46,17 41,12 48,12" 
              fill="#FFD700"
              opacity="0.9"
            />
          )}
        </svg>
        
        {/* Sparkle animation for 15+ years */}
        {tier.tier >= 7 && animate && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-1 h-1 bg-yellow-300 rounded-full animate-ping" style={{ animationDelay: '0s' }} />
            <div className="absolute top-1/4 right-0 w-1 h-1 bg-pink-300 rounded-full animate-ping" style={{ animationDelay: '0.3s' }} />
            <div className="absolute bottom-1/4 left-0 w-1 h-1 bg-blue-300 rounded-full animate-ping" style={{ animationDelay: '0.6s' }} />
            <div className="absolute bottom-0 right-1/4 w-1 h-1 bg-purple-300 rounded-full animate-ping" style={{ animationDelay: '0.9s' }} />
          </div>
        )}
      </div>
      
      {/* Label - Just "X Years" without tier name */}
      {showLabel && (
        <p 
          className="font-bold leading-tight text-center"
          style={{ 
            fontSize: s.label,
            background: tier.bgGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          {displayYears} {displayYears === 1 ? 'Year' : 'Years'}
        </p>
      )}
    </div>
  );
};

/**
 * Calculate years of service from first working day
 * @param {string} firstWorkingDay - ISO date string (YYYY-MM-DD)
 * @returns {number} Years of service (can be decimal for partial years)
 */
export const calculateYearsOfService = (firstWorkingDay) => {
  if (!firstWorkingDay) return 0;
  
  try {
    const startDate = new Date(firstWorkingDay);
    const today = new Date();
    
    // Check if valid date
    if (isNaN(startDate.getTime())) return 0;
    
    // Calculate difference in years
    const diffTime = today - startDate;
    const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);
    
    // Return 0 if negative (future date) or less than 1 year
    if (diffYears < 1) return 0;
    
    return Math.floor(diffYears);
  } catch (error) {
    console.error('Error calculating years of service:', error);
    return 0;
  }
};

// Memoize the component to prevent unnecessary re-renders
const MemoizedTenureBadge = memo(TenureBadge);
MemoizedTenureBadge.displayName = 'TenureBadge';

export default MemoizedTenureBadge;
